import * as PIXI from "pixi.js";
import { loadArrowAssets, setupArrow } from "../actors/arrow";
import { loadHeartAssets, setupHeart } from "../actors/heart";
import { loadLogoAssets, setupLogo } from "../actors/logo";
import { loadLoliAssets, setupLoli } from "../actors/loli";
import { setupMonologue } from "../actors/monologue";
import { loadPlayerAssets, setupPlayer } from "../actors/player";
import { loadScaffold, setupScaffold } from "../actors/scaffold";
import { charaSize, screenHeight, screenWidth } from "../constants";
import { CollisionManager, createRect } from "../managers/collisionManager";
import { HeartManager } from "../managers/heartManager";
import { InputManager } from "../managers/inputManager";
import { LoadManager } from "../managers/loadManager";
import { LoliManager } from "../managers/loliManager";
import { MonologueManager } from "../managers/monologueManager";
import { ScrollManager } from "../managers/scrollManager";
import { SoundManager } from "../managers/soundManager";
import { TimeCountManager } from "../managers/timeCountManager";
import { TitleStateManager } from "../managers/titleStateManager";
import { sleep } from "../utils/sleep";
import { setupStageUI } from "./ui";

export type SetupTitleProps = Readonly<{
  parent: PIXI.Container;
  ticker: PIXI.Ticker;
  soundManager: SoundManager;
  goNext: () => void;
}>;

export function setupTitle({
  parent,
  ticker,
  soundManager,
  goNext,
}: SetupTitleProps) {
  const loadManager = new LoadManager();

  const logoAssets = loadLogoAssets(loadManager);
  const playerAssets = loadPlayerAssets(loadManager);
  const arrowAssets = loadArrowAssets(loadManager);
  const loliAssets = loadLoliAssets(loadManager);
  const scaffoldAssets = loadScaffold(loadManager);
  const heartAssets = loadHeartAssets(loadManager);

  let dispose: () => void = () => {};

  loadManager.waitForLoad().then(() => {
    const stageContainer = new PIXI.Container();

    const inputManager = new InputManager(ticker);
    const stageWidth = screenWidth * 2;
    const stageHeight = screenHeight;
    const playerX = screenWidth / 2;
    const playerY = screenHeight - charaSize / 2;
    const scrollManager = new ScrollManager(
      stageWidth,
      stageHeight,
      screenWidth,
      screenHeight,
      playerX,
      playerY,
    );
    const collisionManager = new CollisionManager(stageHeight);

    const titleStateManager = new TitleStateManager();

    const loliManager = new LoliManager();
    const monologueManager = new MonologueManager();

    const heartManager = new HeartManager();

    const timeCountManager = new TimeCountManager(ticker);
    timeCountManager.startCountDown(Infinity);

    const disposes: Array<() => void> = [];
    disposes.push(
      setupStageUI({
        heartAssets,
        parent,
        ticker,
        heartManager,
        timeCountManager,
      }),
      setupLogo({
        logoAssets,
        parent: stageContainer,
        x: playerX,
        y: charaSize * 4,
      }),
      setupArrow({
        arrowAssets,
        parent: stageContainer,
        x: playerX,
        y: playerY - charaSize * 3,
        angle: 180,
      }),
      setupArrow({
        arrowAssets,
        parent: stageContainer,
        x: screenWidth * 1.25 - charaSize,
        y: screenHeight - charaSize * 3.5,
        angle: 90,
      }),
      setupScaffold({
        scaffoldAssets,
        parent: stageContainer,
        ticker,
        collisionManager,
        x: screenWidth * 1.25,
        y: screenHeight - charaSize * 4,
        width: screenWidth / 2,
      }),
      setupLoli({
        loliAssets,
        loliManager,
        parent: stageContainer,
        ticker,
        x: screenWidth * 1.5,
        y: screenHeight - charaSize * 5,
      }),
      setupPlayer({
        playerAssets,
        parent: stageContainer,
        ticker,
        soundManager,
        inputManager,
        collisionManager,
        scrollManager,
        x: playerX,
        y: playerY,
        stageWidth,
      }),
    );

    soundManager.playScinarioBGM();

    const eventStartRect = createRect(
      "eventStart",
      screenWidth * 1.25 + charaSize,
      screenHeight - charaSize * 4 - 1,
      screenWidth / 2 - charaSize * 2,
      1,
    );

    const monologue1 = async () => {
      inputManager.pause();
      titleStateManager.next();
      await sleep(250);
      const dispose = setupMonologue({ parent, ticker, monologueManager });
      await sleep(500);
      monologueManager.text = "寂しいよ……くるしいよ……";
      await sleep(2000);
      monologueManager.text = "あなた、ハムスターさん？";
      await sleep(2000);
      monologueManager.text = "助けて……癒しが欲しいの……";
      await sleep(2000);
      monologueManager.text = "";
      dispose();
      titleStateManager.next();
      inputManager.start();
      disposes.push(
        setupHeart({
          heartAssets,
          parent: stageContainer,
          ticker,
          collisionManager,
          soundManager,
          heartManager,
          x: screenWidth * 1.5,
          y: screenHeight - charaSize / 2,
        }),
      );
    };

    const monologue2 = async () => {
      inputManager.pause();
      titleStateManager.next();
      await sleep(250);
      const dispose = setupMonologue({ parent, ticker, monologueManager });
      await sleep(500);
      monologueManager.text = "ありがとう……";
      await sleep(2000);
      monologueManager.text = "でも、もっと癒されたいな……";
      await sleep(2000);
      monologueManager.text = "できたらもっと取ってきてくれないかな……？";
      await sleep(2000);
      monologueManager.text = "イタズラ好きのネコにだけは気をつけて……";
      await sleep(2000);
      monologueManager.text = "";
      dispose();
      goNext();
    };

    const onTick = () => {
      stageContainer.x = scrollManager.containerX;
      stageContainer.y = scrollManager.containerY;
      const collides = collisionManager.check(eventStartRect);
      if (collides.some((c) => c.type === "player")) {
        if (titleStateManager.state === "crying") {
          monologue1();
        } else if (
          titleStateManager.state === "searching-heart" &&
          heartManager.num > 0
        ) {
          monologue2();
        }
      }
    };

    parent.addChild(stageContainer);
    ticker.add(onTick);

    dispose = () => {
      soundManager.stopSchinarioBGM();
      inputManager.dispose();
      disposes.forEach((d) => d());
      parent.removeChild(stageContainer);
      ticker.remove(onTick);
    };
  });

  return () => dispose();
}
